import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, } from 'react';
import { StyleSheet } from 'react-native';
import { AppView, rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, StatusBar, withAnimatedNavigationHeader, } from 'design-system';
import { IconSettings } from 'design-system/icons/IconSettings';
import { getBreaches, getMonitoredAccounts } from 'actions';
import useRequiredBenefitCallback from 'features/premium/hooks/useRequiredBenefitCallback';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import { logQuest } from 'features/quests/actions';
import { didSetupFraudDetectionQuest } from 'features/quests/store/selectors';
import Animated from 'react-native-reanimated';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import ListLoader from 'design-system/ListLoader';
import BreachCard from './components/BreachCard';
import NoBreaches from './components/NoBreaches';
import UpgradeToUnlockFraud from './components/UpgradeToUnlockFraud';
import { isWeb } from '../../constants';
const keyExtractor = (item) => String(item.id);
const DataProtectionScreen = withAnimatedNavigationHeader(({ navigation, scrollHandler }) => {
    const pageCount = useAppSelector((store) => store.dataProtection.breachPageCount);
    const dispatch = useAppDispatch();
    const store = useAppStore();
    useEffect(() => {
        if (!didSetupFraudDetectionQuest(store.getState().quests.quests)) {
            dispatch(logQuest('SETUP_FRAUD_DETECT'));
        }
    }, []);
    const { isUnlocked, onPressUpgrade } = useShouldShowUpgrade({
        benefitId: 'dataProtection',
    });
    const onBreachPressed = useRequiredBenefitCallback(useCallback((breach) => {
        navigation.navigateDeprecated('BreachScreen', { breach });
    }, []), 'dataProtection');
    const [isFetching, setIsFetching] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [breachesToShow, setBreachesToShow] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const resetBreaches = useCallback(() => {
        setCurrentPage(1);
        setIsFetching(true);
        dispatch(getMonitoredAccounts());
        dispatch(getBreaches(1)).then((e) => {
            setIsFetching(false);
            setIsFetchingMore(false);
            if (e?.type === 'GET_BREACHES_SUCCESS') {
                setBreachesToShow(e?.payload?.breaches);
            }
        });
    }, []);
    const onOpenSettings = useCallback(() => {
        navigation.navigateDeprecated('DataProtectionSettings', {
            onResetBreaches: resetBreaches,
        });
    }, [resetBreaches]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<NavigationHeaderRightIconButton onPress={onOpenSettings}>
              <IconSettings colorKey="primary"/>
            </NavigationHeaderRightIconButton>),
        });
    }, [onOpenSettings]);
    useEffect(() => {
        // wait for the navigation transition to end
        const timeout = setTimeout(() => {
            setCurrentPage(1);
            setIsFetching(true);
            dispatch(getBreaches(1)).then((e) => {
                setIsFetching(false);
                setIsFetchingMore(false);
                if (e?.type === 'GET_BREACHES_SUCCESS') {
                    setBreachesToShow(e?.payload?.breaches);
                }
            });
        }, 300);
        return () => {
            clearTimeout(timeout);
        };
    }, [onOpenSettings, resetBreaches]);
    let onEndReachedCalledDuringMomentum = false;
    const onEndReached = () => {
        if (!isFetching &&
            !isFetchingMore &&
            !onEndReachedCalledDuringMomentum &&
            currentPage < pageCount) {
            setIsFetchingMore(true);
            dispatch(getBreaches(currentPage + 1)).then((e) => {
                setIsFetching(false);
                setIsFetchingMore(false);
                onEndReachedCalledDuringMomentum = true;
                if (e?.type === 'GET_BREACHES_SUCCESS') {
                    setCurrentPage(currentPage + 1);
                    setBreachesToShow([
                        ...breachesToShow,
                        ...(e?.payload?.breaches || {}),
                    ]);
                }
            });
        }
    };
    const renderItem = useCallback(({ item }) => (<BreachCard key={item.id} breach={item} onPress={onBreachPressed} isUnlocked={isUnlocked}/>), [isUnlocked, onBreachPressed]);
    const ListHeaderComponent = useMemo(() => (<UpgradeToUnlockFraud hideCard={!breachesToShow.length} isUnlocked={isUnlocked} onPressUpgrade={onPressUpgrade}/>), [breachesToShow.length, isUnlocked, onPressUpgrade]);
    const ListEmptyComponent = useMemo(() => isFetching ? (<ListLoader />) : (<NoBreaches onAddDetails={onOpenSettings}/>), [isFetching, onOpenSettings]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, styles.contentContainerStyle, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
          <StatusBar barStyle="dark-content"/>
          <Animated.FlatList data={breachesToShow} renderItem={renderItem} onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} keyExtractor={keyExtractor} ListHeaderComponent={ListHeaderComponent} ListEmptyComponent={ListEmptyComponent} onEndReached={onEndReached} onEndReachedThreshold={0.8} contentContainerStyle={contentContainerStyle}/>
        </AppView>);
}, ({ colors }) => ({
    title: 'Fraud detection',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    contentContainerStyle: {
        paddingTop: rem(16),
    },
});
export default DataProtectionScreen;
